import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import s from './NavigationDesktop.module.scss';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames';
import { GradientContext, ContactContext } from '../../containers/BasePage';
import { trackMenuInteraction, trackMenuContact } from '../../utils/dataLayer';
const NavigationDesktop = ({
  navigation = [],
  siteTitle,
  modifiers = [],
  languages = [],
}) => {
  const navRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuHeights, setMenuHeights] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleMenuToggle = (menuType) => {
    setOpenMenu((prevMenu) => (prevMenu === menuType ? null : menuType));
  };

  const updateMenuHeight = useCallback((menuType, height) => {
    setMenuHeights((prevHeights) => {
      if (prevHeights[menuType] !== height) {
        return { ...prevHeights, [menuType]: height };
      }
      return prevHeights;
    });
  }, []);

  const getCurrentHeight = () => {
    if (openMenu) {
      return menuHeights[openMenu] || 0;
    }
    return 0;
  };

  const toggleLanguageDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleLanguageKeyDown = (e) => {
    if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(null);
    setDropdownOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const classes = classNames(
    s.Root,
    modifiers.map((m) => s[m]),
    {
      [s.IsOpen]: openMenu,
    }
  );

  return (
    <>
      <div
        className={classNames(s.Overlay, {
          [s.IsOpen]: openMenu,
        })}
      />
      <header
        ref={navRef}
        className={classes}
        style={{ paddingBottom: `${getCurrentHeight()}px` }}>
        <nav className={s.NavigationDesktop} aria-label="Menu">
          <div className={s.LogoContainer}>
            <a href="/" className={s.LogoLink}>
              <Image
                width={172}
                height={48}
                src="/img/logo-desktop.svg"
                alt="Stockholm Business Region Logotype"
                className={s.Logo}
                loading="lazy"
              />
            </a>
            <div className={s.Divider} />
            {/* <h3 className={s.Title}>{siteTitle}</h3> */}
          </div>
          <div className={s.LinkContainer}>
            {navigation?.map((navigationItem, i) => (
              <NavigationDesktopItem
                key={i}
                {...navigationItem}
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                updateMenuHeight={updateMenuHeight}
                handleCloseMenu={handleCloseMenu}
              />
            ))}
            {/* Hide until the english version is ready */}
            <div className={s.Language}>
              <div
                className={classNames(s.LanguageWrapper, {
                  [s.IsOpen]: dropdownOpen,
                })}>
                <button
                  type="button"
                  className={classNames(s.LanguageDropdown, {
                    [s.IsOpen]: dropdownOpen,
                  })}
                  onClick={toggleLanguageDropdown}
                  onKeyDown={handleLanguageKeyDown}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true">
                  <Image
                    width={16}
                    height={16}
                    src={`/img/icon-web.svg`}
                    alt=""
                  />
                  <span className={s.LanguageText}>
                    {languages.find((lang) => lang.active)?.title ||
                      'Select Language'}
                  </span>
                  <Image
                    className={s.LanguageChevron}
                    width={16}
                    height={16}
                    src={`/img/icon-chevron-down.svg`}
                    alt=""
                  />
                </button>
                {dropdownOpen && (
                  <div className={s.LanguageList}>
                    {languages
                      .filter((lang) => !lang.active)
                      .map((lang) => (
                        <Link key={lang.lang_code} href={lang.href}>
                          <span className={s.LanguageItem}>{lang.title}</span>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

NavigationDesktop.propTypes = {
  navigation: PropTypes.array.isRequired,
};

const NavigationDesktopItem = ({
  title,
  href = '',
  children = [],
  isActive,
  isCurrent,
  megaMenuType = 'none',
  openMenu,
  handleMenuToggle,
  updateMenuHeight,
  showEventIcon,
  handleCloseMenu,
}) => {
  const megaMenuRef = useRef(null);
  const hasChildren = children.length > 0;
  const isOpen = openMenu === title;

  useEffect(() => {
    if (megaMenuRef.current && isOpen) {
      const height = megaMenuRef.current.scrollHeight;
      updateMenuHeight(title, height);
    } else if (!isOpen) {
      updateMenuHeight(title, 0);
    }
  }, [isOpen, title, updateMenuHeight]);

  const classes = classNames(s.NavigationDesktopLink, {
    [s.IsActive]: isActive,
    [s.IsOpen]: isOpen,
  });

  return (
    <div className={classNames(s.NavigationItem, { [s.IsOpen]: isOpen })}>
      <a
        href={hasChildren ? '' : href}
        className={classes}
        aria-current={isCurrent ? 'page' : false}
        onClick={(e) => {
          if (hasChildren) {
            e.preventDefault();
            handleMenuToggle(title);
          }
          if (!hasChildren && href) {
            trackMenuInteraction(1, title);
            handleCloseMenu();
          }
          if (hasChildren && !isOpen) {
            trackMenuInteraction(1, title);
          }
        }}>
        {showEventIcon && (
          <Image
            className={s.EventIcon}
            width={20}
            height={20}
            src={`/img/icon-calendar.svg`}
            alt=""
          />
        )}
        {title}
        {hasChildren && (
          <span
            className={classNames(s.Chevron, {
              [s.ChevronOpen]: isOpen,
            })}
          />
        )}
      </a>
      {hasChildren && (
        <div
          ref={megaMenuRef}
          className={classNames(s.MegaMenu, {
            [s.IsOpen]: isOpen,
            [s.IsHidden]: !isOpen,
          })}>
          <MegaMenu
            title={title}
            href={href}
            children={children}
            megaMenuType={megaMenuType}
            onBackClick={() => handleCloseMenu()}
          />
        </div>
      )}
    </div>
  );
};

NavigationDesktopItem.propTypes = {
  title: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  children: PropTypes.array.isRequired,
  megaMenuType: PropTypes.string.isRequired,
  openMenu: PropTypes.string,
  handleMenuToggle: PropTypes.func.isRequired,
  updateMenuHeight: PropTypes.func.isRequired,
};

const MegaMenu = ({ children = [], onBackClick }) => {
  return (
    <div className={s.MegaMenu}>
      <div className={s.MegaMenuContainer}>
        <div className={s.MegaMenuContentColumns}>
          {children.map((section, i) => (
            <div key={i} className={s.MegaMenuColumn}>
              {section.href ? (
                <a
                  href={section.href}
                  className={s.MegaMenuTitle}
                  onClick={() => {
                    onBackClick?.();
                  }}>
                  {section.title}
                </a>
              ) : (
                <span className={s.MegaMenuTitle}>{section.title}</span>
              )}

              {section.children && section.children.length > 0 && (
                <ul className={s.MegaMenuLinks}>
                  {section.children.map((link, j) => (
                    <li key={j} className={s.MegaMenuLinkItem}>
                      <a
                        className={s.MegaMenuLink}
                        href={link.href}
                        onClick={() => {
                          trackMenuInteraction(2, link.title);
                          onBackClick?.();
                        }}>
                        <Image
                          className={s.MegaMenuLinkArrow}
                          width={24}
                          height={24}
                          src={`/img/icon-arrow-right-white.svg`}
                          alt=""
                        />
                        <span className={s.MegaMenuLinkText}>{link.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NavigationDesktop;
